/**
 * 桌面端用户登录 API（desktop-login P1）。手机号 + 密码 → 会话令牌。
 * 跟 fabric-server::routes::auth 对齐。
 */
import { apiRequest } from './client'

export interface SessionUser {
  ulid: string
  display_name: string
  /** owner / admin / staff / readonly */
  role: string
}

export interface SessionTenant {
  ulid: string
  shop_name: string
}

/** GET /auth/me 形 + login 成功载荷共用的会话身份。 */
export interface AuthSession {
  user: SessionUser
  tenant: SessionTenant
  /** 登录手机号(账号安全页显示)。GET /auth/me 返;login 成功载荷暂不带,故可选。 */
  phone?: string
  must_change_password: boolean
}

export interface LoginSuccess {
  token: string
  expires_at: string
  must_change_password: boolean
  user: SessionUser
  tenant: SessionTenant
}

export interface TenantCandidate {
  tenant_ulid: string
  shop_name: string
}

/** 登录返回:成功(带 token)或需选档口(同手机号多档口都验过密码)。 */
export type LoginResponse = LoginSuccess | { candidates: TenantCandidate[] }

export function isPickTenant(r: LoginResponse): r is { candidates: TenantCandidate[] } {
  return (r as { candidates?: unknown }).candidates !== undefined
}

export function login(
  phone: string,
  password: string,
  tenantUlid?: string
): Promise<LoginResponse> {
  return apiRequest<LoginResponse>('/auth/login', {
    method: 'POST',
    jsonBody: { phone, password, tenant_ulid: tenantUlid },
    // 登录的 401 是"账号或密码错误",交页面显示,不触发全局重登引导(P1-6)。
    skipAuthRedirect: true
  })
}

/** GET /auth/me —— 校验令牌 + 拿当前身份。 */
export function authMe(): Promise<AuthSession> {
  // 免费档设备令牌在 /auth/me 上本就 401(无 user 会话),bootstrap 据此判定免费档;
  // 不能把它当令牌过期清掉有效设备令牌,故 skip 全局 401 引导(P1-6)。
  return apiRequest<AuthSession>('/auth/me', { skipAuthRedirect: true })
}

export function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<{ ok: boolean }> {
  return apiRequest('/auth/change-password', {
    method: 'POST',
    jsonBody: { old_password: oldPassword, new_password: newPassword }
  })
}

export function logout(): Promise<{ ok: boolean }> {
  return apiRequest('/auth/logout', { method: 'POST' })
}

// ─── 已登录设备 / 会话管理（账号安全 P2）────────────────────────────────

export interface UserSessionItem {
  id: number
  /** desktop / mobile / web；旧会话可能 null */
  device_type: string | null
  last_used_at: string
  created_at: string
  /** 是否发起本次请求的当前设备 */
  is_current: boolean
}

/** GET /auth/sessions —— 列我自己的活跃会话(当前排最前)。 */
export function listSessions(): Promise<{ items: UserSessionItem[] }> {
  return apiRequest('/auth/sessions')
}

/** DELETE /auth/sessions/:id —— 下线我自己的某条会话。 */
export function revokeSession(id: number): Promise<{ ok: boolean }> {
  return apiRequest(`/auth/sessions/${id}`, { method: 'DELETE' })
}

/** POST /auth/revoke-other-sessions —— 退出我其它所有设备(留当前)。 */
export function revokeOtherSessions(): Promise<{ revoked: number }> {
  return apiRequest('/auth/revoke-other-sessions', { method: 'POST' })
}
